import { useState, useCallback } from "react"
import { unstable_batchedUpdates } from "react-dom"

const handleResponse = async requests => {
  const responses = await Promise.all(
    Object.entries(requests).map(async ([k, v]) => {
      const response = await Promise.allSettled(
        [].concat(v).map(r => r.then(x => ({ ...x, timestamp: Date.now() })))
      )

      const responseData = response.filter(res => res.status === "fulfilled").map(res => res.value)

      const responseError = response
        .filter(res => res.status === "rejected")
        .map(res => ({
          status: 500,
          title: "Request Error",
          detail: res.reason.message || res.reason.errors
        }))

      const errors = [
        ...responseData.filter(d => d.errors).flatMap(d => d.errors),
        ...responseError
      ]

      const data = responseData.filter(d => d[k] || d.data).reverse(t => t.timestamp)

      return [
        k,
        {
          responseErrors: errors,
          responseData: data,
          hasError: errors.length ? true : false,
          error: errors.length ? errors[0] : null,
          data: data.length ? data[0][k] || data[0].data : null
        }
      ]
    })
  )

  return Object.fromEntries(responses)
}

export const useSettingPane = (initialState = {}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(initialState.isLoading || false)
  const [status, setStatus] = useState(null)
  const [error, setError] = useState(null)
  const [data, setData] = useState(initialState.data || {})

  const handleIsEditing = useCallback(newValue => setIsEditing(newValue))

  const handleStatus = useCallback(newStatus => setStatus(newStatus))

  const handleCancel = useCallback(async () => {
    // Unstable sounds bad, but seems recomended by the react team when updating multiple state items
    //  https://github.com/facebook/react/issues/16377
    unstable_batchedUpdates(() => {
      setIsEditing(false)
      setError(null)
      setStatus(null)
      setData({
        ...data
      })
    })
  })

  const handleSave = useCallback(async requests => {
    setStatus("saving")
    const response = await handleResponse(requests)

    updateState(response)
    setTimeout(() => setStatus(null), 3000)
    return response
  })

  const handleFetch = useCallback(async requests => {
    setIsLoading(true)
    const response = await handleResponse(requests)

    updateState(response)
    return response
  })

  const updateState = response => {
    const responseError = Object.values(response).find(x => x.hasError) || null

    unstable_batchedUpdates(() => {
      isEditing && setIsEditing(responseError ? true : false)
      isEditing && setStatus(responseError ? "error" : "success")
      setIsLoading(false)
      setError(responseError ? responseError.error : null)
      setData({
        ...data,
        ...Object.fromEntries(
          Object.entries(response)
            .filter(([_k, v]) => !v.hasError)
            .map(([k, v]) => [k, v.data])
        )
      })
    })
  }

  const handleDataChange = (newData = {}) => setData({ ...data, ...newData })

  return {
    isEditing,
    isLoading,
    status,
    error,
    handleIsEditing,
    handleStatus,
    handleSave,
    handleFetch,
    handleCancel,
    handleDataChange,
    data
  }
}
